<template>
    <editor-field-frame :label="props.label || $t('editor.fields.action.default_label')" :hint="props.hint">
        <v-card class="editor-section-card" variant="outlined">
            <v-card-text>
                <!--
                    Verständlicher Ablauf statt Symbolgruppe:
                    1. Buttontext
                    2. Was soll der Button tun?
                    3. Nur bei Bedarf das passende Linkziel
                -->
                <editor-field-frame :label="$t('editor.fields.action.text')"
                                    :hint="$t('editor.fields.action.text_hint')"
                                    :control-id="ids.text">
                    <v-text-field v-model="text"
                                  :id="ids.text"
                                  :disabled="type === 'hidden'"
                                  variant="outlined"
                                  density="comfortable"
                                  hide-details />
                </editor-field-frame>

                <editor-field-frame :label="$t('editor.fields.action.type')"
                                    :hint="actionHint">
                    <v-radio-group v-model="type" hide-details density="comfortable">
                        <v-radio v-for="option in actionOptions" :key="option.value"
                                 :value="option.value" :label="option.label" />
                    </v-radio-group>
                </editor-field-frame>

                <!-- Linkziel nur, wenn die gewählte Aktion es braucht -->
                <editor-field-frame v-if="type === 'external'"
                                    :label="$t('editor.fields.action.external')"
                                    :hint="$t('editor.fields.action.external_hint')"
                                    :error="externalError"
                                    :control-id="ids.external">
                    <!-- i18n-ignore: Der Platzhalter ist ein Adressformat, kein Text -->
                    <v-text-field placeholder="https://"
                                  v-model="href"
                                  :id="ids.external"
                                  type="url"
                                  variant="outlined"
                                  density="comfortable"
                                  hide-details />
                </editor-field-frame>

                <editor-field-frame v-else-if="type === 'internal'"
                                    :label="$t('editor.fields.action.internal')"
                                    :hint="$t('editor.fields.action.internal_hint')"
                                    :error="internalError"
                                    :control-id="ids.internal">
                    <v-text-field v-model="href"
                                  :id="ids.internal"
                                  variant="outlined"
                                  density="comfortable"
                                  hide-details />
                </editor-field-frame>
            </v-card-text>
        </v-card>
    </editor-field-frame>
</template>

<script setup>
import {computed} from "vue";
import EditorFieldFrame from "./EditorFieldFrame.vue";
import {mandatory_props, useEditorField, editorControlId} from "../../utils/editorConfigFieldHelpers";
import {useI18n} from "@/i18n";

const {t} = useI18n();

/**
 * Die gespeicherten Werte bleiben unverändert ("next", "internal",
 * "external", "hidden") - nur die Darstellung wird verständlich.
 *
 * computed, damit die Beschriftungen beim Sprachwechsel mitziehen.
 */
const actionOptions = computed(() => [
    {value: 'next', label: t('editor.fields.action.options.next')},
    {value: 'internal', label: t('editor.fields.action.options.internal')},
    {value: 'external', label: t('editor.fields.action.options.external')},
    {value: 'hidden', label: t('editor.fields.action.options.hidden')},
]);

const props = defineProps(mandatory_props);
const {childValue, jsonPath} = useEditorField(props);

/**
 * Eindeutige Kennungen je Feldinstanz - mehrere Button-Aktionen auf einer
 * Seite dürfen sich ihre Label-Verknüpfung nicht teilen.
 */
const ids = computed(() => ({
    text: editorControlId('action-text', jsonPath.value),
    external: editorControlId('action-href-ext', jsonPath.value),
    internal: editorControlId('action-href-int', jsonPath.value),
}));

const href = childValue('href');
const text = childValue('text');
const type = childValue('type');

const actionHint = computed(() => {
    const option = actionOptions.value.find(o => o.value === type.value);

    return option ? null : t('editor.fields.action.errors.no_type');
});

const externalError = computed(() => {
    if (type.value !== 'external') return null;
    if ( ! href.value) return t('editor.fields.action.errors.external_missing');

    try {
        const url = new URL(href.value);
        if ( ! ['http:', 'https:'].includes(url.protocol)) {
            return t('editor.fields.action.errors.external_protocol');
        }
    } catch (e) {
        return t('editor.fields.action.errors.external_invalid');
    }

    return null;
});

const internalError = computed(() => {
    if (type.value !== 'internal') return null;

    return href.value ? null : t('editor.fields.action.errors.internal_missing');
});
</script>
